import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { compose } from 'redux';
import { Field, reduxForm, formValueSelector, change, untouch } from 'redux-form';
import { FormattedMessage, injectIntl } from 'react-intl';
import { numericality, required } from 'redux-form-validators';
import { BasicModal, DefaultButton, PageContentPreloader } from 'common/components';
import { ReduxSelectField, ReduxTextField } from 'common/components/form-fields';
import { withTheme } from 'common/styling/theme';
import { FormMessageHOC } from 'common/HOCs';
import {
  getTradersTransferInfo,
  getInnerTransferOptions,
  getMultiWalletTransferOptions,
  createTradersTransfer,
} from '../../_redux';
import InternalTransferConfirmation from '../InternalTransferConfirmation';

import { staticStyles, getDynamicStyles } from './style';

const selector = formValueSelector('traders-transfer-form');
const getFixedNumber = (num, decimal) => Number(Number(num).toFixed(decimal));
const getWalletAccountOptionValue = id => `walletAccount:${id}`;
const isWalletAccountOption = value => typeof value === 'string' && value.startsWith('walletAccount:');
const getWalletAccountId = value => {
  const id = value.replace('walletAccount:', '');
  const numericId = Number(id);

  return Number.isNaN(numericId) ? id : numericId;
};
const getTransferAccountValue = value => (isWalletAccountOption(value) ? getWalletAccountId(value) : value);

const ExternalTransfer = ({
  formValues,
  innerTransferOptions,
  walletTransferOptions,
  tradersTransferInfo,
  innerTransferOptionsAreLoaded,
  walletTransferOptionsAreLoaded,
  tradersTransferInfoIsLoaded,
  getInnerTransferOptions,
  getWalletTransferOptions,
  getTradersTransferInfo,
  isMultiWalletTransfer,
  handleSubmit,
  pristine,
  submitting,
  invalid,
  transfersError,
  dispatch,
  error,
  theme,
  intl,
}) => {
  const dynamicStyles = Object.keys(theme).length ? getDynamicStyles(theme) : ` `;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isReceiveAmountNegative, setIsReceiveAmountNegative] = useState(false);
  const [sendCurrency, setSendCurrency] = useState('');
  const submit = handleSubmit(values =>
    createTradersTransfer(
      {
        ...values,
        senderAccount: getTransferAccountValue(values.senderAccount),
      },
      dispatch
    )
  );

  useEffect(() => {
    getInnerTransferOptions();
  }, [getInnerTransferOptions]);

  useEffect(() => {
    if (isMultiWalletTransfer) {
      getWalletTransferOptions();
    }
  }, [getWalletTransferOptions, isMultiWalletTransfer]);

  const toggleTransferConfirmationModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  const getAccountInfo = account => {
    if (isWalletAccountOption(account)) {
      const walletAccountId = getWalletAccountId(account);
      return walletTransferOptions.find(item => String(item.id) === String(walletAccountId));
    }

    return innerTransferOptions.find(item => item.id === account);
  };

  const getExternalTransferCommission = (...values) => {
    const { senderAccount, recipientAccount, sendAmount } = formValues;
    if (values[3] === 'senderAccount' && values[0] !== recipientAccount && recipientAccount) {
      getTradersTransferInfo({
        senderAccount: getTransferAccountValue(values[0]),
        recipientAccount,
      });
      setSendCurrency(getAccountInfo(values[0]).balance?.currency ?? 'USD');
    } else if (values[3] === 'recipientAccount' && values[1] && values[1] !== senderAccount && senderAccount) {
      getTradersTransferInfo({
        recipientAccount: values[1],
        senderAccount: getTransferAccountValue(senderAccount),
      });
      setSendCurrency(getAccountInfo(senderAccount).balance?.currency ?? 'USD');
    }
    if (sendAmount) {
      dispatch(change('traders-transfer-form', 'sendAmount', null));
      dispatch(untouch('traders-transfer-form', 'sendAmount'));
      dispatch(change('traders-transfer-form', 'receiveAmount', null));
    }
  };
  const calculateReceiveAmount = (_, value) => {
    const sendAmount = parseFloat(value);
    if (
      typeof sendAmount === 'number' &&
      sendAmount <= tradersTransferInfo.availableAmount &&
      tradersTransferInfo.details
    ) {
      const { exchangeCoefficient = 1, commission } = tradersTransferInfo.details;
      let calculatedCommission =
        (sendAmount / 100) * commission.percentage +
        commission.fix * tradersTransferInfo.details.exchangeCoefficientForFix;
      if (calculatedCommission > tradersTransferInfo.details.maxCommission.amount)
        calculatedCommission = tradersTransferInfo.details.maxCommission.amount;
      if (calculatedCommission < tradersTransferInfo.details.minCommission.amount)
        calculatedCommission = tradersTransferInfo.details.minCommission.amount;
      const receiveAmount = (sendAmount - calculatedCommission) * exchangeCoefficient;
      let roundedReceiveAmount = 0;
      if (receiveAmount > 0) {
        roundedReceiveAmount = getFixedNumber(receiveAmount, 2);
        setIsReceiveAmountNegative(false);
      } else {
        setIsReceiveAmountNegative(true);
      }
      dispatch(change('traders-transfer-form', 'receiveAmount', roundedReceiveAmount.toString()));
    }
  };

  const walletAccountOptions = walletTransferOptions.map(item => ({
    ...item,
    id: getWalletAccountOptionValue(item.id),
  }));

  const accounts = [...(isMultiWalletTransfer ? walletAccountOptions : []), ...innerTransferOptions].map(item => ({
    label: `${item.accountType} ${item.login} ${
      item.balance ? ` - ${item.balance.amount}${item.balance.currency}` : ''
    }`,
    value: item.id,
  }));

  return (
    <PageContentPreloader
      ready={innerTransferOptionsAreLoaded && (!isMultiWalletTransfer || walletTransferOptionsAreLoaded)}
      type="bigLine">
      <form className="ExternalTransfer" onSubmit={submit}>
        <div className="ExternalTransfer__line">
          <Field
            component={ReduxSelectField}
            name="senderAccount"
            textId="clientAccountSender"
            options={accounts}
            onChange={getExternalTransferCommission}
            disabled={submitting}
            validate={[required()]}
          />
          <Field
            component={ReduxTextField}
            type="text"
            name="recipientAccount"
            textId="clientAccountRecipient"
            validate={[required()]}
            disabled={submitting}
            onBlur={getExternalTransferCommission}
          />
        </div>
        <div className="ExternalTransfer__line">
          <Field
            component={ReduxTextField}
            type="text"
            name="sendAmount"
            textId="clientSendAmount"
            validate={[
              required(),
              numericality({
                msg: tradersTransferInfo.requestedAmount
                  ? {
                      '<=': intl.formatMessage(
                        { id: 'availableAmountForTransfer' },
                        {
                          available: tradersTransferInfo.availableAmount,
                          requested: tradersTransferInfo.requestedAmount,
                        }
                      ),
                    }
                  : {},
                '>=': 0,
                '<=': tradersTransferInfo.availableAmount ?? 10000,
              }),
            ]}
            disabled={submitting || !tradersTransferInfoIsLoaded}
            onChange={calculateReceiveAmount}
            description={sendCurrency}
          />
          <Field
            component={ReduxTextField}
            type="text"
            name="receiveAmount"
            textId="justWillReceive"
            validate={[required()]}
            disabled
          />
        </div>
        {transfersError && <span className="ExternalTransfer__error">{transfersError}</span>}
        {tradersTransferInfo.details && (
          <div className="ExternalTransfer__description">
            <FormattedMessage
              id={
                tradersTransferInfo.details.exchangeRate
                  ? 'commissionForInternalTransferDifferentCurrencies'
                  : 'commissionForInternalTransferSameCurrencies'
              }
              values={{
                commission: `${tradersTransferInfo.details.commission.percentage}% + ${tradersTransferInfo.details.commission.fix} USD.`,
              }}>
              {txt => <span>{txt}</span>}
            </FormattedMessage>
            {tradersTransferInfo.details.exchangeRate && <span>{tradersTransferInfo.details.exchangeRate}</span>}
            {tradersTransferInfo.details.minCommission && tradersTransferInfo.details.maxCommission && (
              <FormattedMessage
                id="commissionLimitDescription"
                values={{
                  min: `${tradersTransferInfo.details.minCommission.amount} ${tradersTransferInfo.details.minCommission.currency}`,
                  max: `${tradersTransferInfo.details.maxCommission.amount} ${tradersTransferInfo.details.maxCommission.currency}`,
                }}>
                {txt => <span>{txt}</span>}
              </FormattedMessage>
            )}
          </div>
        )}

        <DefaultButton
          textId="clientSend"
          onClick={toggleTransferConfirmationModal}
          type="button"
          disabled={isReceiveAmountNegative || invalid || pristine || submitting || !tradersTransferInfoIsLoaded}
          filled
        />
      </form>

      <BasicModal
        isOpen={isModalOpen}
        captionId="internalTransferConfirmation"
        onRequestClose={toggleTransferConfirmationModal}>
        <InternalTransferConfirmation
          payload={{
            senderAccount: getAccountInfo(formValues.senderAccount),
            recipientAccount: formValues.recipientAccount,
            sendAmount: `${formValues.sendAmount} ${sendCurrency}`,
            receiveAmount: formValues.receiveAmount,
          }}
          submitting={submitting}
          handleSubmit={submit}
          handleCancel={toggleTransferConfirmationModal}
          error={error}
        />
      </BasicModal>

      <style jsx>{staticStyles}</style>
      <style jsx>{dynamicStyles}</style>
    </PageContentPreloader>
  );
};

ExternalTransfer.propTypes = {
  formValues: PropTypes.object.isRequired,
  tradersTransferInfo: PropTypes.object.isRequired,
  innerTransferOptions: PropTypes.array.isRequired,
  walletTransferOptions: PropTypes.array.isRequired,
  tradersTransferInfoIsLoaded: PropTypes.bool.isRequired,
  innerTransferOptionsAreLoaded: PropTypes.bool.isRequired,
  walletTransferOptionsAreLoaded: PropTypes.bool.isRequired,
  transfersError: PropTypes.string.isRequired,
  getInnerTransferOptions: PropTypes.func.isRequired,
  getWalletTransferOptions: PropTypes.func.isRequired,
  getTradersTransferInfo: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
  pristine: PropTypes.bool.isRequired,
  submitting: PropTypes.bool.isRequired,
  error: PropTypes.bool,
  isMultiWalletTransfer: PropTypes.bool,
  invalid: PropTypes.bool.isRequired,
  intl: PropTypes.object.isRequired,
  theme: PropTypes.object,
};

ExternalTransfer.defaultProps = {
  isMultiWalletTransfer: false,
  error: '',
  theme: '',
};

export default compose(
  withTheme(),
  withRouter,
  injectIntl,
  FormMessageHOC,
  connect(
    state => ({
      formValues: selector(state, 'senderAccount', 'recipientAccount', 'sendAmount', 'receiveAmount'),
      innerTransferOptions: state.client.transfers.innerTransferOptions,
      innerTransferOptionsAreLoaded: state.client.transfers.innerTransferOptionsAreLoaded,
      walletTransferOptions: state.client.transfers.multiWalletTransferOptions,
      walletTransferOptionsAreLoaded: state.client.transfers.multiWalletTransferOptionsAreLoaded,
      tradersTransferInfo: state.client.transfers.tradersTransferInfo,
      tradersTransferInfoIsLoaded: state.client.transfers.tradersTransferInfoIsLoaded,
      transfersError: state.client.transfers.error,
    }),
    {
      getInnerTransferOptions: data => getInnerTransferOptions.request(data),
      getWalletTransferOptions: () => getMultiWalletTransferOptions.request(),
      getTradersTransferInfo: data => getTradersTransferInfo.request(data),
    }
  ),
  reduxForm({
    form: 'traders-transfer-form',
    onSubmitSuccess(result, dispatch, props) {
      props.showFormSuccessMessage(props.successMessageTextId);
      props.history.push('/payments?tab=transfers');
    },
  })
)(ExternalTransfer);
export { ExternalTransfer };
